import { firestoreService } from '@/utils/firestoreService'
import type { CommodityType } from '@/types/CommodityType'

/**
 * CommodityType Service - Provides business logic for commodity type operations
 */
export function useCommodityTypeService() {
  return {
    /**
     * Get all active commodity types
     * Filters out inactive and deleted commodity types
     */
    async getActiveCommodityTypes(): Promise<CommodityType[]> {
      try {
        const commodityTypes = await firestoreService.query<CommodityType>('commodity_types', [
          { field: 'is_active', op: '==', value: true },
          { field: 'is_deleted', op: '==', value: false },
        ])
        return commodityTypes
      } catch (error) {
        console.error('Failed to load commodity types:', error)
        throw new Error('Failed to load commodity types')
      }
    },

    /**
     * Get commodity type by ID
     */
    async getCommodityType(id: string): Promise<CommodityType | null> {
      try {
        return await firestoreService.get<CommodityType>('commodity_types', id)
      } catch (error) {
        console.error('Failed to load commodity type:', error)
        throw new Error('Failed to load commodity type')
      }
    },

    /**
     * Create a new commodity type
     */
    async createCommodityType(data: Partial<CommodityType>): Promise<CommodityType> {
      try {
        const now = new Date()
        const commodityType: CommodityType = {
          name: data.name || '',
          description: data.description || '',
          category: data.category || '',
          current_price_per_lb: data.current_price_per_lb || 0,
          is_favorite: data.is_favorite || false,
          is_active: true,
          is_deleted: false,
          created_at: data.created_at || (now as Date),
          updated_at: data.updated_at || (now as Date),
          ...data,
        }

        const id = await firestoreService.add<CommodityType>('commodity_types', commodityType)
        return { ...commodityType, id }
      } catch (error) {
        console.error('Failed to create commodity type:', error)
        throw new Error('Failed to create commodity type')
      }
    },

    /**
     * Update a commodity type
     */
    async updateCommodityType(id: string, data: Partial<CommodityType>): Promise<CommodityType> {
      try {
        const updates = {
          ...data,
          updated_at: new Date(),
        }

        await firestoreService.update<CommodityType>('commodity_types', id, updates)
        return (await this.getCommodityType(id)) as CommodityType
      } catch (error) {
        console.error('Failed to update commodity type:', error)
        throw new Error('Failed to update commodity type')
      }
    },

    /**
     * Soft delete a commodity type
     */
    async deleteCommodityType(id: string): Promise<void> {
      try {
        await firestoreService.update<CommodityType>('commodity_types', id, {
          is_deleted: true,
          is_active: false,
          updated_at: new Date(),
        })
      } catch (error) {
        console.error('Failed to delete commodity type:', error)
        throw new Error('Failed to delete commodity type')
      }
    },
  }
}
